import { useRef, useCallback, useEffect } from 'react';

interface UseDragToScrollOptions {
  direction?: 'horizontal' | 'vertical' | 'both';
  sensitivity?: number;
}

export const useDragToScroll = (options: UseDragToScrollOptions = {}) => {
  const { direction = 'horizontal', sensitivity = 1 } = options;
  const elementRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const scrollStart = useRef({ left: 0, top: 0 });

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!elementRef.current) return;

    isDragging.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    scrollStart.current = {
      left: elementRef.current.scrollLeft,
      top: elementRef.current.scrollTop
    };

    // Change cursor to grabbing
    elementRef.current.style.cursor = 'grabbing';
    elementRef.current.style.userSelect = 'none';

    // Prevent text selection
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current || !elementRef.current) return;

      const deltaX = (e.clientX - startPos.current.x) * sensitivity;
      const deltaY = (e.clientY - startPos.current.y) * sensitivity;

      if (direction === 'horizontal' || direction === 'both') {
        elementRef.current.scrollLeft = scrollStart.current.left - deltaX;
      }

      if (direction === 'vertical' || direction === 'both') {
        elementRef.current.scrollTop = scrollStart.current.top - deltaY;
      }
    },
    [direction, sensitivity]
  );

  const handleMouseUp = useCallback(() => {
    if (!elementRef.current) return;

    isDragging.current = false;
    elementRef.current.style.cursor = 'grab';
    elementRef.current.style.userSelect = '';
  }, []);

  const setupEventListeners = useCallback(
    (element: HTMLDivElement) => {
      // Set initial cursor
      element.style.cursor = 'grab';

      // Add event listeners
      element.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp);

      return () => {
        element.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseUp);
      };
    },
    [handleMouseDown, handleMouseMove, handleMouseUp]
  );

  const callbackRef = useCallback(
    (element: HTMLDivElement | null) => {
      // Clean up previous element if it exists
      if (elementRef.current) {
        elementRef.current.style.cursor = '';
        elementRef.current.style.userSelect = '';
      }

      elementRef.current = element;

      if (element) {
        const cleanup = setupEventListeners(element);
        // Store cleanup function on the element for later use
        (element as any)._dragScrollCleanup = cleanup;
      }
    },
    [setupEventListeners]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (elementRef.current && (elementRef.current as any)._dragScrollCleanup) {
        (elementRef.current as any)._dragScrollCleanup();
      }
    };
  }, []);

  return callbackRef;
};
